import { useId, useMemo } from 'react';
import { BASELINE_Y, PAD_X, VIEW_W, stationX, stationY, throughLinePath } from './careerThroughLinePath';
import clsx from './clsx';
import styles from './CareerThroughLine.module.css';

// =============================================================================
// CareerThroughLine — the horizontal "career through-line" (LANE DNA-BUILD,
// Phase 2). Layout-logic-VERBATIM port of apps/web/src/components/viz/
// CareerThroughLine.tsx: same station spacing, same Catmull-Rom-ish smooth
// path, same gold-pill stacking geometry — the ONLY change is the palette,
// which moves from hardcoded hex to the --tl-* theme token system (this
// app's own vocabulary; apps/web has no such system, so this re-skin is real,
// bounded work per the plan, not a rename)
// (docs/design/dna-interview-port-2026-07-05.md §3 Phase 2).
//
// Reads LEFT → RIGHT. A timeline spine of career eras (the user's roles) as
// stations on a baseline; a smooth through-line thread weaving THROUGH the
// stations (the literal "through-line"); the gold the user undersells
// branching UP off each era; and a readiness chip top-right.
//
// PURE PRESENTATION (no-frontend-bandaids law): every datum here is SERVED.
// The through-line text + the era-to-era threads come from the synthesised
// dna_profiles row, the eras come from the user's roles, the gold comes from
// the DNA facets (isNormalToMe), the readiness number from /corpus/readiness.
// This component computes ONLY layout geometry (x/y of each station, the
// curve path) — it never recomputes the DNA, never decides what is gold,
// never scores readiness. If a field is absent the station/branch/chip
// simply renders less (empty state), never an invented value.
//
// Gold-pill token: per the plan's own Phase 2 note, "the gold you undersell"
// reuses --tl-warm/--tl-warm-soft rather than minting a 7th token pair —
// themes.ts's own header comment defines `warm` as "an honest-fact...
// tracked/applied marker," semantically close enough to ship without a new
// token; flagged there as worth confirming with Jason, not a blocker.
// =============================================================================

/** A gold strength branching up off an era — the win the user undersells.
 *  Maps from a DNA facet where isNormalToMe === 1 (or a gold_first achievement). */
export interface ThroughLineGold {
  id: string;
  /** Short label shown in the pill (the facet/achievement headline, trimmed). */
  label: string;
}

/** One career era — a station on the spine. Maps from a role (DnaEra). */
export interface ThroughLineEra {
  /** roleId, or a stable synthetic key for origins. Used to match threads + gold. */
  id: string;
  /** Station label, e.g. "Acme — Staff Engineer". */
  label: string;
  /** Optional sublabel under the station (company or the date range). */
  sublabel?: string;
  /** YYYY or YYYY-MM; ordering is by the caller, this is display-only. */
  period?: string;
  /** The gold strengths branching up off this era. */
  gold?: ThroughLineGold[];
  /**
   * How this era reached the timeline:
   *  - 'captured'  → solid station (excavated in the interview / has facets).
   *  - 'imported'  → dashed ghost station (filled in from a resume, not yet
   *                  excavated). Visually distinguished per the spec.
   * Defaults to 'captured' when the caller has no provenance signal.
   */
  origin?: 'captured' | 'imported';
}

/** A through-line thread connecting two eras — the continuity the synthesis found.
 *  Maps from dna_profiles.threads ({ from, to, thread }); from/to are era labels. */
export interface ThroughLineThread {
  /** Era id (or label) the thread runs from. */
  from: string;
  /** Era id (or label) the thread runs to. */
  to: string;
  /** The one-line continuity, e.g. "the same refusal to let a system fall over". */
  thread: string;
}

export interface ThroughLineReadiness {
  /** 0..100, served from /corpus/readiness (never self-reported). */
  score: number;
  /** true once the corpus is ready to tailor against. */
  ready: boolean;
  /** Optional single "what's thin" line, e.g. "almost — 1 era thin". */
  note?: string;
}

export interface CareerThroughLineProps {
  /** The woven storyline (dna_profiles.through_line). Shown above the spine. */
  throughLine?: string | null;
  /** The eras, oldest → newest (the caller orders them). */
  eras: ThroughLineEra[];
  /** Era-to-era threads (dna_profiles.threads). */
  threads?: ThroughLineThread[];
  /** The readiness chip, top-right. Omit to hide the chip. */
  readiness?: ThroughLineReadiness | null;
  className?: string;
}

// ── Layout constants specific to this component (VIEW_W/PAD_X/BASELINE_Y
//    live in careerThroughLinePath.ts, shared with the extracted geometry) ──
const STATION_R = 9; // station dot radius
const GOLD_LANE_TOP = 70; // highest a gold pill stacks to
const GOLD_GAP = 34; // vertical gap between stacked gold pills
const GOLD_PILL_W = 168; // gold pill width
const GOLD_PILL_H = 26;

export function CareerThroughLine({
  throughLine,
  eras,
  threads = [],
  readiness,
  className,
}: CareerThroughLineProps) {
  const gradientId = useId();
  const ghostPatternId = useId();

  const layout = useMemo(() => {
    const count = eras.length;
    return eras.map((era, i) => ({
      era,
      x: stationX(i, count),
      y: stationY(i, count),
    }));
  }, [eras]);

  const pathD = useMemo(() => throughLinePath(layout.map((s) => ({ x: s.x, y: s.y }))), [layout]);

  // Resolve a thread's from/to (era id OR label) to station x-positions, so the
  // continuity arcs sit under the two stations they connect.
  const threadArcs = useMemo(() => {
    const byKey = new Map<string, (typeof layout)[number]>();
    for (const s of layout) {
      byKey.set(s.era.id, s);
      byKey.set(s.era.label, s);
    }
    return threads
      .map((t, i) => {
        const a = byKey.get(t.from);
        const b = byKey.get(t.to);
        if (!a || !b) return null;
        const x1 = Math.min(a.x, b.x);
        const x2 = Math.max(a.x, b.x);
        const mid = (x1 + x2) / 2;
        const depth = 44 + Math.min(40, (x2 - x1) * 0.08);
        return {
          key: `${t.from}-${t.to}-${i}`,
          d: `M ${x1} ${BASELINE_Y + 26} Q ${mid} ${BASELINE_Y + 26 + depth} ${x2} ${BASELINE_Y + 26}`,
          labelX: mid,
          labelY: BASELINE_Y + 26 + depth + 4,
          thread: t.thread,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [threads, layout]);

  const isEmpty = eras.length === 0;

  return (
    <div className={clsx(styles.card, className)}>
      {/* Header: the woven storyline + the readiness chip top-right */}
      <div className={styles.header}>
        <div className={styles.headerMain}>
          <div className={styles.eyebrow}>
            <SparkIcon />
            Your through-line
          </div>
          {throughLine ? (
            <p className={styles.throughLineText}>{throughLine}</p>
          ) : (
            <p className={styles.throughLineEmpty}>
              The through-line appears once you've talked through a few eras.
            </p>
          )}
        </div>
        {readiness ? <ReadinessChip readiness={readiness} /> : null}
      </div>

      {isEmpty ? (
        <EmptyTimeline />
      ) : (
        <svg
          viewBox={`0 0 ${VIEW_W} 420`}
          className={styles.svg}
          role="img"
          aria-label="Career through-line: a horizontal timeline of your career eras with the gold you undersell branching up off each."
        >
          <title>Career through-line</title>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" className={styles.gradStart} />
              <stop offset="55%" className={styles.gradMid} />
              <stop offset="100%" className={styles.gradEnd} />
            </linearGradient>
            {/* hatch fill for ghost (imported, not-yet-excavated) stations */}
            <pattern
              id={ghostPatternId}
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" className={styles.ghostPatternBg} />
              <line x1="0" y1="0" x2="0" y2="6" className={styles.ghostPatternLine} />
            </pattern>
          </defs>

          {/* the era-to-era continuity threads, arcing under the spine */}
          {threadArcs.map((arc) => (
            <g key={arc.key} className={styles.threadGroup}>
              <path d={arc.d} className={styles.threadPath} />
              <foreignObject
                x={arc.labelX - 130}
                y={arc.labelY}
                width={260}
                height={40}
                className={styles.foreignOverflow}
              >
                <div className={styles.threadLabel}>{arc.thread}</div>
              </foreignObject>
            </g>
          ))}

          {/* the through-line thread itself, weaving through the stations */}
          <path
            d={pathD}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={3}
            strokeLinecap="round"
            className={styles.throughLinePath}
          />

          {/* the baseline tick the stations sit on (subtle ground line) */}
          <line
            x1={PAD_X - 20}
            y1={BASELINE_Y}
            x2={VIEW_W - PAD_X + 20}
            y2={BASELINE_Y}
            className={styles.baseline}
          />

          {layout.map((s, i) => {
            const imported = s.era.origin === 'imported';
            const gold = s.era.gold ?? [];
            return (
              <g
                key={s.era.id}
                className={styles.stationGroup}
                style={{ animationDelay: `${i * 70}ms` }}
              >
                {/* connector from the station down to the baseline label rail */}
                <line x1={s.x} y1={s.y} x2={s.x} y2={BASELINE_Y + 14} className={styles.connector} />

                {/* gold strengths: stacked pills above the station, each tied by a
                    thin line back to the station dot */}
                {gold.map((g, gi) => {
                  const py = GOLD_LANE_TOP + gi * GOLD_GAP;
                  return (
                    <g key={g.id}>
                      <line
                        x1={s.x}
                        y1={s.y}
                        x2={s.x}
                        y2={py + GOLD_PILL_H / 2}
                        className={styles.goldLine}
                      />
                      <foreignObject
                        x={s.x - GOLD_PILL_W / 2}
                        y={py}
                        width={GOLD_PILL_W}
                        height={GOLD_PILL_H}
                        className={styles.foreignOverflow}
                      >
                        <div title={g.label} className={styles.goldPill}>
                          <SparkIcon small />
                          <span className={styles.goldPillLabel}>{g.label}</span>
                        </div>
                      </foreignObject>
                    </g>
                  );
                })}

                {/* the station dot — solid (captured) vs dashed ghost (imported) */}
                <circle
                  cx={s.x}
                  cy={s.y}
                  r={STATION_R}
                  className={imported ? styles.stationDotImported : styles.stationDotCaptured}
                  fill={imported ? `url(#${ghostPatternId})` : undefined}
                  strokeDasharray={imported ? '3 3' : undefined}
                />
                {gold.length > 0 ? (
                  // a small gold ring on stations that caught gold
                  <circle cx={s.x} cy={s.y} r={STATION_R + 4} className={styles.goldRing} />
                ) : null}

                {/* station label rail under the baseline */}
                <foreignObject
                  x={s.x - 90}
                  y={BASELINE_Y + 16}
                  width={180}
                  height={56}
                  className={styles.foreignOverflow}
                >
                  <div className={styles.stationLabelWrap}>
                    <div
                      className={imported ? styles.stationLabelImported : styles.stationLabel}
                      title={s.era.label}
                    >
                      {s.era.label}
                    </div>
                    {s.era.sublabel ? (
                      <div className={styles.stationSublabel} title={s.era.sublabel}>
                        {s.era.sublabel}
                      </div>
                    ) : null}
                    {imported ? <div className={styles.importedTag}>imported · thin</div> : null}
                  </div>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      )}

      {/* legend: read the two station treatments */}
      {!isEmpty ? (
        <div className={styles.legend}>
          <span className={styles.legendItem}>
            <span className={styles.legendDotCaptured} />
            captured in interview
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendDotImported} />
            imported from resume
          </span>
          <span className={styles.legendItem}>
            <SparkIcon small />
            gold you undersell
          </span>
        </div>
      ) : null}
    </div>
  );
}

function ReadinessChip({ readiness }: { readiness: ThroughLineReadiness }) {
  const { score, ready, note } = readiness;
  return (
    <div className={clsx(styles.readinessChip, ready ? styles.readinessReady : styles.readinessAlmost)}>
      <div className={styles.readinessScore}>{score}%</div>
      <div className={styles.readinessNote}>{note ?? (ready ? 'ready to move on' : 'almost there')}</div>
    </div>
  );
}

function EmptyTimeline() {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <SparkIcon />
      </div>
      <p className={styles.emptyText}>
        Your career through-line will draw itself here as you talk through your eras — or import an
        old resume to fill in the early ones.
      </p>
    </div>
  );
}

function SparkIcon({ small }: { small?: boolean }) {
  const size = small ? 12 : 14;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l2.4 7.2H22l-6 4.4 2.3 7.2L12 16.4l-6.3 4.4 2.3-7.2-6-4.4h7.6z" />
    </svg>
  );
}
